import { features } from "./features";

/**
 * The navigation, in one place, so the sidebar, the command palette and the
 * breadcrumb cannot disagree about what the product contains.
 */

export interface NavItem {
  /** The label in the sidebar and the palette. */
  label: string;
  /** Appended to `/p/<project>`. */
  path: string;
  /** A hugeicons export name, resolved in `components/shell/nav-icon.tsx`. */
  icon: NavIconName;
  /** A one-line hint, shown in the command palette. */
  hint: string;
  /**
   * True when a child route still highlights this item, which is the router's
   * `activeOptions.exact: false`. Overview is the project root, so it must be
   * exact or it owns every page in the project.
   */
  nested?: boolean;
}

export type NavIconName =
  | "dashboard"
  | "plug"
  | "message"
  | "mail"
  | "calendar"
  | "contacts"
  | "people"
  | "link"
  | "webhook"
  | "key"
  | "log"
  | "settings";

export interface NavGroup {
  /** The label above the group; the first group has none. */
  title?: string;
  items: NavItem[];
}

/**
 * Four groups, in the order a customer works through them: what is happening,
 * what the accounts hold, how their product talks to Aux0, and the workspace.
 *
 * The three data surfaces are the launch scope — mail, calendar and contacts.
 * Messaging and People are the deferred channels' screens and appear only
 * behind `features.messaging`.
 */
export const NAV: NavGroup[] = [
  {
    items: [
      {
        label: "Overview",
        path: "",
        icon: "dashboard",
        hint: "Traffic, account health and what needs attention",
      },
      {
        label: "Accounts",
        path: "/accounts",
        icon: "plug",
        hint: "Every connected account, its quota and its health",
        nested: true,
      },
    ],
  },
  {
    title: "Data",
    items: [
      {
        label: "Mail",
        path: "/mail",
        icon: "mail",
        hint: "Gmail, Outlook, iCloud, Fastmail, Zoho and IMAP threads",
        nested: true,
      },
      {
        label: "Calendar",
        path: "/calendar",
        icon: "calendar",
        hint: "Events and availability across Google and Outlook calendars",
        nested: true,
      },
      {
        label: "Contacts",
        path: "/contacts",
        icon: "contacts",
        hint: "Address books, groups and the addresses behind autocomplete",
        nested: true,
      },
      ...(features.messaging
        ? [
            {
              label: "Messaging",
              path: "/inbox",
              icon: "message" as const,
              hint: "Chat conversations on the account-based channels",
              nested: true,
            },
            {
              label: "People",
              path: "/people",
              icon: "people" as const,
              hint: "Profiles, invitations and posts on the social providers",
            },
          ]
        : []),
    ],
  },
  {
    title: "Integration",
    items: [
      {
        label: "Connect",
        path: "/connect",
        icon: "link",
        hint: "The hosted auth flow your users see",
      },
      {
        label: "Webhooks",
        path: "/webhooks",
        icon: "webhook",
        hint: "Endpoints, delivery history and replay",
        nested: true,
      },
      {
        label: "API keys",
        path: "/keys",
        icon: "key",
        hint: "Keys per environment, with capability scopes",
      },
      {
        label: "Logs",
        path: "/logs",
        icon: "log",
        hint: "Every action, by a person or a key",
      },
    ],
  },
  {
    title: "Workspace",
    items: [
      {
        label: "Settings",
        path: "/settings",
        icon: "settings",
        hint: "Project, members and billing",
        nested: true,
      },
    ],
  },
];

export const NAV_ITEMS = NAV.flatMap((group) => group.items);

/** `/p/sales/accounts`, the form every link in the app takes. */
export function projectPath(project: string, path = ""): string {
  return `/p/${project}${path}`;
}

/**
 * The section a path is in, for the breadcrumb: the deepest nav item whose
 * base the path starts with.
 */
export function navSection(pathname: string, project: string): NavItem | undefined {
  return NAV_ITEMS.filter(
    (item) => item.path && pathname.startsWith(projectPath(project, item.path)),
  ).sort((a, b) => b.path.length - a.path.length)[0];
}
